// Data feedback modal ("Report data issue") — BUILD-ORDER-data-feedback.
//
// One click selects a category, the comment stays optional, send shows a
// thank-you toast. Categories the user already reported today render as
// "Reported" (checked, disabled) so nothing is double-posted; the backend
// upserts per (reporter, entity, category, day) as the safety net.
//
// Modal pattern follows the house convention (ContactSupportModal.js):
// own component, isOpen/onClose, `if (!isOpen) return null;`, fixed overlay,
// mobile bottom-sheet / desktop dialog — NO new modal framework.
// Mobile (375px): the panel is the standard w-full bottom sheet, the category
// buttons stack in one column (grid-cols-1 sm:grid-cols-2), everything else
// scrolls inside max-h-[90vh] — nothing shrinks or truncates.
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  dataFeedbackService,
  DATA_FEEDBACK_CATEGORIES,
} from "../../services/dataFeedbackService";

const COMMENT_MAX = 500;

const ReportDataIssueModal = ({
  isOpen,
  onClose,
  entityType, // "player" | "club" | "coach"
  wyscoutId,
  tsCoachId, // coach lane: TheSports string id instead of wyscout_id
  entityName,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportedCategories, setReportedCategories] = useState([]);

  // Fresh status on every open: which categories did this user already file
  // today for this entity? (Server truth, not localStorage — same approach
  // as the radar profile-status probe.)
  useEffect(() => {
    if (!isOpen || (!wyscoutId && !tsCoachId)) return undefined;
    const controller = new AbortController();
    dataFeedbackService
      .getStatus({ entityType, wyscoutId, tsCoachId }, controller.signal)
      .then((res) => {
        if (res?.success) setReportedCategories(res.data?.categories || []);
      })
      .catch(() => {
        /* aborted — modal closed before the probe returned */
      });
    return () => controller.abort();
  }, [isOpen, entityType, wyscoutId, tsCoachId]);

  if (!isOpen) return null;

  const alreadyReportedToday = reportedCategories.length > 0;

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await dataFeedbackService.reportIssue({
        entityType,
        wyscoutId,
        tsCoachId,
        category: selectedCategory,
        comment,
      });
      if (result.success) {
        toast.success("Thanks! Your report has been sent.");
        setReportedCategories(result.data?.categories || [selectedCategory]);
        setSelectedCategory("");
        setComment("");
        onClose();
      } else {
        toast.error(result.error || "Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#0A0F2D4D] backdrop-blur-md transition-opacity duration-300 ease-out"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[500px] max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl p-8 transform transition-all duration-300 ease-out motion-safe:animate-[slideUp_0.3s_ease-out_forwards]"
      >
        <div className="w-full text-center text-[#1A212F] text-xl font-bold">
          Report data issue
        </div>
        {entityName && (
          <div className="mt-2 w-full text-center text-[#505050] text-sm">
            {entityName}
          </div>
        )}

        {alreadyReportedToday && (
          <div className="mt-4 w-full rounded-lg bg-[#F3F7FF] border border-[#D6E6FF] p-3 text-center text-sm text-[#1A212F]">
            You already reported this profile today.
          </div>
        )}

        <div className="w-full flex flex-col gap-2 mt-5">
          <label className="text-black text-[16px] font-medium">
            What is wrong?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DATA_FEEDBACK_CATEGORIES.map(({ value, label }) => {
              const reported = reportedCategories.includes(value);
              const selected = selectedCategory === value;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={reported}
                  data-state={
                    reported ? "reported" : selected ? "selected" : "idle"
                  }
                  onClick={() => setSelectedCategory(selected ? "" : value)}
                  className={`min-h-[44px] rounded-lg border px-3 py-2 text-left text-[14px] transition-colors ${
                    reported
                      ? "border-[#D6E6FF] bg-[#F3F7FF] text-[#505050] cursor-not-allowed"
                      : selected
                      ? "border-[#2283FF] bg-[#2283FF] text-white"
                      : "border-[#E6E6E6] bg-white text-[#1E1E1E] hover:bg-gray-50"
                  }`}
                >
                  {label}
                  {reported && (
                    <span className="ml-2 text-[#16A34A] font-semibold">
                      ✓ Reported
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 mt-5">
          <label className="text-black text-[16px] font-medium">
            Comment (optional)
          </label>
          <textarea
            value={comment}
            maxLength={COMMENT_MAX}
            onChange={(e) => setComment(e.target.value)}
            className="resize-none w-full h-[100px] border border-[#ECEEF6] rounded-xl p-2 text-[16px] placeholder:text-[#505050]"
            placeholder="Describe the issue"
          />
        </div>

        <div className="flex w-full justify-between mt-5">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer w-[48%] bg-white border border-[#E6E6E6] rounded-lg p-2 text-center text-[14px] text-[#1E1E1E]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`cursor-pointer w-[48%] rounded-lg p-2 text-center text-[14px] ${
              isSubmitting
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-[#2283FF] text-white"
            }`}
          >
            {isSubmitting ? "Sending..." : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportDataIssueModal;
